// Netlify Functions API configuration
const API_BASE = '/.netlify/functions';

export const API_ENDPOINTS = {
  // Auth
  signup: `${API_BASE}/auth/signup`,
  login: `${API_BASE}/auth/login`,
  verify: `${API_BASE}/auth/verify`,
  changePassword: `${API_BASE}/auth/password`,
  
  // Users
  users: `${API_BASE}/auth/users`,
  deleteUser: (email: string) => `${API_BASE}/auth/users/${encodeURIComponent(email)}`,
  
  // Images
  images: `${API_BASE}/images`,
  uploadImage: `${API_BASE}/images`,
  deleteImage: (id: string) => `${API_BASE}/images/${id}`,
  
  // Bookings
  bookings: `${API_BASE}/bookings`,
  updateBooking: (id: string) => `${API_BASE}/bookings/${id}`,
  deleteBooking: (id: string) => `${API_BASE}/bookings/${id}`,
  
  // Business Hours
  businessHours: `${API_BASE}/business-hours`,

  // OpenAI proxy
  openai: `${API_BASE}/openai`,
};

// Track if we're using fallback mode
let usingFallbackMode = false;

// Check if backend is available
async function checkBackendAvailable(): Promise<boolean> {
  try {
    const response = await fetch(API_ENDPOINTS.images, { method: 'GET' });
    const contentType = response.headers.get('content-type');
    
    // If we get HTML, backend isn't available
    if (contentType && contentType.includes('text/html')) {
      return false;
    }
    
    return true;
  } catch (error) {
    return false;
  }
}

// Auto-detect and set fallback mode on first API call
let backendCheckPromise: Promise<boolean> | null = null;

export async function isBackendAvailable(): Promise<boolean> {
  if (backendCheckPromise === null) {
    backendCheckPromise = checkBackendAvailable();
  }
  return backendCheckPromise;
}

export function isUsingFallback(): boolean {
  return usingFallbackMode;
}

export function setUsingFallback(value: boolean): void {
  usingFallbackMode = value;
}

// Auth token storage helper
export const authToken = {
  get: (): string | null => {
    return localStorage.getItem('authToken');
  },
  set: (token: string): void => {
    localStorage.setItem('authToken', token);
  },
  remove: (): void => {
    localStorage.removeItem('authToken');
  },
};

// Helper to make API calls with error handling
export async function apiCall(endpoint: string, options?: RequestInit) {
  try {
    const response = await fetch(endpoint, options);
    
    // Check if response is HTML (error page)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      throw new Error(`API endpoint not found: ${endpoint}. Switching to fallback mode.`);
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    throw error;
  }
}